import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Email, SupervisorAccount } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { studentsAPI } from '../services/api';
import { formatDateTimeInViewerTZ, getViewerTimezone } from '../utils/dateTimeUtils';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import { useAuth } from '../contexts/AuthContext';

function formatSentAt(iso, timezone) {
  if (!iso) return '—';
  return formatDateTimeInViewerTZ(iso, getViewerTimezone(timezone)) || iso;
}

function ChannelStatus({ label, channel, showActions, onNotify, notifying, canNotifyParent, fees, feeCurrency }) {
  const { user } = useAuth();
  const sent = channel?.sent;
  const isResend = sent && channel?.hasResend;
  const ack = channel?.acknowledgment;

  return (
    <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="subtitle2">{label}</Typography>
        <Chip
          size="small"
          label={
            sent
              ? isResend
                ? `Sent (${channel.count}x, last resend)`
                : `Sent${channel.count > 1 ? ` (${channel.count}x)` : ''}`
              : 'Not sent'
          }
          color={sent ? 'success' : 'default'}
          variant={sent ? 'filled' : 'outlined'}
        />
      </Stack>
      {sent && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Last: {formatSentAt(channel.lastSentAt, user?.timezone)}
          {channel.events?.[channel.events.length - 1]?.recipientSummary
            ? ` · ${channel.events[channel.events.length - 1].recipientSummary}`
            : ''}
        </Typography>
      )}
      {ack && ack.status !== 'not_applicable' && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Supervisor ack:{' '}
          {ack.status === 'acknowledged'
            ? `Yes${ack.acknowledgedBy ? ` (${ack.acknowledgedBy})` : ''}`
            : ack.status === 'pending'
              ? 'Pending'
              : '—'}
        </Typography>
      )}
      {showActions && onNotify && (
        <Button
          size="small"
          variant="outlined"
          sx={{ mt: 1 }}
          onClick={onNotify}
          disabled={notifying}
          startIcon={notifying ? <CircularProgress size={16} /> : <Email />}
        >
          {sent ? 'Resend' : 'Send'}
        </Button>
      )}
    </Box>
  );
}

export default function EnrollmentWorkflowPanel({
  studentId,
  enrollmentStatus,
  fees,
  feeCurrency,
  canManage,
  onStatusChange,
  onBeforeNotify,
}) {
  const [notifyingSupervisor, setNotifyingSupervisor] = useState(false);
  const [notifyingParent, setNotifyingParent] = useState(false);

  if (!canManage && !enrollmentStatus) return null;

  // Welcome-only parent email (no fees). Subscription confirmation carries fee details.
  const canNotifyParent = true;

  const refresh = async () => {
    if (onStatusChange) await onStatusChange();
  };

  const handleNotifySupervisor = async () => {
    setNotifyingSupervisor(true);
    try {
      if (onBeforeNotify) {
        const ok = await onBeforeNotify();
        if (!ok) return;
      }
      const res = await studentsAPI.notifyEnrollmentSupervisor(studentId);
      toast.success(res.data?.message || 'Supervisors notified');
      await refresh();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to notify supervisors'));
    } finally {
      setNotifyingSupervisor(false);
    }
  };

  const handleNotifyParent = async () => {
    setNotifyingParent(true);
    try {
      if (onBeforeNotify) {
        const ok = await onBeforeNotify();
        if (!ok) return;
      }
      const res = await studentsAPI.notifyEnrollmentParent(studentId);
      toast.success(res.data?.message || 'Parent welcome email sent');
      await refresh();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to notify parent'));
    } finally {
      setNotifyingParent(false);
    }
  };

  const status = enrollmentStatus || {
    salesSubmit: { sent: false },
    supervisor: { sent: false },
    parent: { sent: false },
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
        Enrollment workflow
      </Typography>
      <ChannelStatus
        label="Sales alert (form submit)"
        channel={status.salesSubmit}
      />
      <ChannelStatus
        label="Notify supervisor"
        channel={status.supervisor}
        showActions={canManage}
        onNotify={handleNotifySupervisor}
        notifying={notifyingSupervisor}
      />
      <ChannelStatus
        label="Notify parent"
        channel={status.parent}
        showActions={canManage}
        onNotify={handleNotifyParent}
        notifying={notifyingParent}
        canNotifyParent={canNotifyParent}
        fees={fees}
        feeCurrency={feeCurrency}
      />
      {canManage && (
        <Typography variant="caption" color="text.secondary" display="block">
          Parent email is welcome-only (no fees). Subscription confirmation carries plan and payment details.
          Resend is allowed; prior sends are shown above.
        </Typography>
      )}
      {!canManage && status.supervisor?.acknowledgment?.status === 'pending' && (
        <Alert severity="info" sx={{ mt: 1 }} icon={<SupervisorAccount />}>
          Open this student profile to acknowledge the enrollment review notification.
        </Alert>
      )}
    </Box>
  );
}
